import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader
} from '@clientbase/clientbase-library'
import {
  Box,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@mui/material'
import { DataService } from 'api/DataService'
import { BillingsParams } from 'models/Billings'
import { RecurrencesParams } from 'models/Recurrences'
import moment from 'moment'

import IconContainer from 'components/IconContainer/IconContainer'
import { Table } from 'components/Table'

import { formatMoney } from 'utils'
import { toastProps } from 'utils/types/toast-props'

import { theme } from 'styles/theme'

interface ModalReactivateRecurrenceProps {
  open: boolean
  onClose: () => void
  recurrence: RecurrencesParams
  onSuccess?: () => void
  onEdit?: () => void
}

const ModalReactivateRecurrence: React.FC<ModalReactivateRecurrenceProps> = ({
  open,
  onClose,
  recurrence,
  onSuccess,
  onEdit
}) => {
  const [resetDirty, setResetDirty] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const dirtyCount = recurrence?.dirtyFutureBillingsCount ?? 0
  const hasFutureCycles = recurrence?.hasFutureCycles !== false

  // Preview dos ciclos a partir de hoje: usamos as billings reais da recorrência
  // (vencimento >= hoje), que serão restauradas na reativação. Diferente do
  // preview_cycles (que projeta o cronograma a partir do first_due_date original,
  // já no passado numa recorrência cancelada), as billings reais carregam os
  // valores efetivos — inclusive os das cobranças editadas manualmente (dirty).
  const futureBillings = useMemo<BillingsParams[]>(() => {
    const today = moment().startOf('day')
    return (recurrence?.billings || [])
      .filter((billing) =>
        billing?.dueDate
          ? moment(billing.dueDate).isSameOrAfter(today, 'day')
          : false
      )
      .sort((a, b) => moment(a.dueDate).valueOf() - moment(b.dueDate).valueOf())
  }, [recurrence])

  const hasFutureBillings = futureBillings.length > 0
  const canReactivate = hasFutureCycles || hasFutureBillings

  useEffect(() => {
    if (open) {
      setResetDirty(false)
    }
  }, [open])

  const handleReactivate = async () => {
    if (isSubmitting) return

    setIsSubmitting(true)
    const toastId = toast.loading('Reativando recorrência...')
    try {
      const response = await DataService({
        type: 'PATCH',
        url: `/v1/recurrences/${recurrence.uuid}/reactivate`,
        data: { reset_dirty: resetDirty }
      })

      if (response.status === 200 && !response.data?.error) {
        toast.update(toastId, {
          render: 'Recorrência reativada com sucesso!',
          type: 'success',
          ...toastProps
        })
        onSuccess?.()
        onClose()
      } else {
        throw new Error(response.data?.message)
      }
    } catch (error) {
      toast.update(toastId, {
        render: 'Houve um erro ao reativar a recorrência.',
        type: 'error',
        ...toastProps
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      disablePortal={false}
      handleClose={onClose}
      open={open}
      width="100%"
      maxWidth="640px"
      maxHeight="90vh"
      height="auto"
    >
      <ModalBody>
        <ModalHeader onClose={onClose}>
          <Stack direction="row" alignItems="center" gap={2}>
            <IconContainer icon="eventRepeat" />
            <Typography variant="mdBold">Reativar recorrência</Typography>
          </Stack>
        </ModalHeader>

        <ModalContent>
          <Stack gap={2} mt={2}>
            {!canReactivate && (
              <Typography variant="smLight" color={theme.palette.neutral[400]}>
                Esta recorrência não possui ciclos futuros para reativar.
              </Typography>
            )}

            {dirtyCount > 0 && (
              <Stack direction="column" spacing={1}>
                <Typography variant="smBold">
                  Cobranças editadas manualmente
                </Typography>
                <Typography
                  variant="smLight"
                  color={theme.palette.neutral[400]}
                >
                  {dirtyCount} cobrança(s) desta recorrência foram editadas
                  manualmente. Como aplicar ao reativar?
                </Typography>
                <RadioGroup
                  value={resetDirty ? 'apply' : 'keep'}
                  onChange={(_, v) => setResetDirty(v === 'apply')}
                >
                  <FormControlLabel
                    value="keep"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="smLight">
                        Manter as edições manuais (não sobrescrever)
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="apply"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="smLight">
                        Limpar as edições e aplicar as configurações da
                        recorrência
                      </Typography>
                    }
                  />
                </RadioGroup>
              </Stack>
            )}

            {!canReactivate && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 2,
                  borderRadius: '8px',
                  border: `1px solid ${theme.palette.alert[300]}`,
                  backgroundColor: theme.palette.alert[500]
                }}
              >
                <Icon
                  icon="error"
                  sx={{
                    fontSize: '20px',
                    color: theme.palette.alert[300],
                    mt: '2px'
                  }}
                />
                <Stack gap={0.5}>
                  <Typography variant="smBold" color={theme.palette.alert[100]}>
                    Sem ciclos futuros para gerar cobranças
                  </Typography>
                  <Typography
                    variant="smLight"
                    color={theme.palette.alert[100]}
                  >
                    Todos os ciclos desta recorrência já venceram. Edite a
                    recorrência para definir novos ciclos e gerar novas
                    cobranças.
                  </Typography>
                </Stack>
              </Box>
            )}

            {canReactivate && (
              <Typography variant="smLight" color={theme.palette.neutral[400]}>
                A recorrência voltará a gerar cobranças a partir de hoje.
                Confira os próximos ciclos:
              </Typography>
            )}

            {hasFutureBillings ? (
              <Box
                sx={{
                  maxHeight: '260px',
                  overflow: 'auto',
                  '& .MuiTableCell-root': { cursor: 'auto !important' }
                }}
              >
                <Table
                  mobileCards
                  header={[
                    { id: 'dueDate', label: 'Vencimento' },
                    { id: 'items', label: 'Descrição' },
                    { id: 'amount', label: 'Valor' },
                    { id: 'edited', label: '' }
                  ]}
                  rows={futureBillings.map((billing) => ({
                    uuid:
                      billing.uuid ||
                      billing.recurrenceCycle?.toString() ||
                      billing.dueDate,
                    dueDate: moment(billing.dueDate).format('DD/MM/YYYY'),
                    items: (
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: theme.palette.neutral[400]
                        }}
                      >
                        {billing.description || recurrence.description || '—'}
                      </Typography>
                    ),
                    amount:
                      Number(billing.amountBilled) === 0 ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography sx={{ color: 'success.main' }}>
                            R$ 0,00
                          </Typography>
                          <Chip
                            label="Bonificado"
                            size="small"
                            color="success"
                            sx={{ fontSize: '10px', height: '20px' }}
                          />
                        </Box>
                      ) : (
                        formatMoney(Number(billing.amountBilled))
                      ),
                    edited: billing.dirty ? (
                      <Chip
                        label="Editada"
                        size="small"
                        sx={{
                          fontSize: '10px',
                          height: '20px',
                          color: theme.palette.alert[100],
                          backgroundColor: theme.palette.alert[500],
                          border: `1px solid ${theme.palette.alert[300]}`
                        }}
                      />
                    ) : (
                      ''
                    )
                  }))}
                  select={{ useSelect: false }}
                  hiddePagination
                  hoverInRow
                  disableFilter
                />
              </Box>
            ) : canReactivate ? (
              <Typography variant="smLight" color={theme.palette.neutral[400]}>
                Os próximos ciclos serão gerados automaticamente após a
                reativação.
              </Typography>
            ) : null}
          </Stack>
        </ModalContent>

        <ModalFooter>
          <Stack width="100%" direction="row" justifyContent="flex-end" gap={1}>
            <Button
              variantButton="neutral"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Voltar
            </Button>
            {canReactivate ? (
              <Button onClick={handleReactivate} loading={isSubmitting}>
                Reativar recorrência
              </Button>
            ) : (
              <Button onClick={() => onEdit?.()}>Editar recorrência</Button>
            )}
          </Stack>
        </ModalFooter>
      </ModalBody>
    </Modal>
  )
}

export default ModalReactivateRecurrence
